import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MessageSquare, Users, FolderOpen, BookOpen, GraduationCap, CheckCircle, UsersRound, Library, UserCheck, ClipboardList, Briefcase, Sparkles, Tag, CalendarDays, Building2, FileText, HelpCircle, MessagesSquare, StickyNote, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import messagingDemo from "../Assets/Home/2.png";
import studyGroupsDemo from "../Assets/Home/3.png";
import fileManagerDemo from "../Assets/Home/4.png";
import clubDemo from "../Assets/Home/5.png";
import eLibraryPreview from "../Assets/Home/6.png";
import facultyNetworkDemo from "../Assets/Home/1.png";
export default function ServicesPage() {
  const [ scrollY, setScrollY ] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: MessageSquare,
      title: 'Real-Time Messaging',
      description: 'Instant communication with peers and faculty',
      features: [
        'One-on-one and group conversations',
        'File sharing and media support',
        'Message history and search',
        'Notification management',
        'Offline message delivery',
      ],
      image: messagingDemo,
    },
    {
      icon: Users,
      title: 'Study Groups & Channels',
      description: 'Collaborative learning spaces for every subject',
      features: [
        'Create and join study groups',
        'Subject-specific channels',
        'Group announcements and updates',
        'Member management',
        'Shared resources and discussions',
      ],
      image: studyGroupsDemo,
    },
    {
      icon: FolderOpen,
      title: 'Smart File Management',
      description: 'Organize your academic materials effortlessly',
      features: [
        'Hierarchical folder structure',
        'Quick file search and filters',
        'Version control for documents',
        'Cloud sync across devices',
        'Secure file storage',
      ],
      image: fileManagerDemo,
    },
    {
      icon: BookOpen,
      title: 'Digital Library',
      description: 'Access thousands of educational resources',
      features: [
        'Extensive book collection',
        'Advanced search capabilities',
        'Bookmarks and annotations',
        'Reading progress tracking',
        'Offline reading mode',
      ],
      image: eLibraryPreview,
    },
    {
      icon: GraduationCap,
      title: 'Faculty Connection',
      description: 'Direct access to educators and mentors',
      features: [
        'Faculty profiles and availability',
        'Office hours scheduling',
        'Direct messaging with professors',
        'Academic guidance and support',
        'Course-specific connections',
      ],
      image: facultyNetworkDemo,
    },
    {
      icon: UsersRound,
      title: "Student Clubs & Communities",
      description: "Join clubs, communities, and interest groups across your institution",
      features: [
        "Official and student-run clubs",
        "Event announcements and updates",
        "Club chat and discussions",
        "Membership management",
        "Cross-department collaboration",
      ],
      image: clubDemo, // replace later if you have a clubs image
    },
    {
      icon: ClipboardList,
      title: 'Student Attendance',
      description: 'Real-time attendance visibility for every student — no registers, no office visits',
      features: [
        'Faculty take attendance per classroom, subject, and lecture — from the app or dashboard',
        'Students see subject-wise percentages update instantly',
        'Every register tied to a real subject from the Subject Registry',
        'Review, edit, and re-sync any past register',
        'One-click CSV/Excel export, emailed to you on request',
      ],
      image: fileManagerDemo,
    },
    {
      icon: Tag,
      title: 'Subject Registry',
      description: 'A shared, institution-wide list of subject codes and names',
      features: [
        'One consistent subject list used across attendance and timetables',
        'Add subjects one at a time or in bulk (up to 50 at once)',
        'Searchable by code or name from any screen that needs a subject',
        'Read-only for faculty and students, managed by admins',
        'Prevents inconsistent subject naming across the institution',
      ],
      image: eLibraryPreview,
    },
    {
      icon: CalendarDays,
      title: 'Classrooms & Timetables',
      description: 'A dedicated group chat, weekly timetable, and Google Meet for every class batch',
      features: [
        'Replaces WhatsApp groups with one organized classroom space',
        'Drag-and-drop weekly timetable builder',
        'Assign a subject teacher to any period — fully optional',
        'Built-in scheduling conflict detection when saving a timetable',
        'One-tap Google Meet links shared straight to the classroom',
      ],
      image: studyGroupsDemo,
    },
    {
      icon: Building2,
      title: 'Departments',
      description: 'Organize students into academic batches with their own announcement channel',
      features: [
        'Each student belongs to exactly one department',
        'Faculty can join multiple departments to stay connected',
        'Dedicated announcement channel per department',
        'Created and managed by Admins and Sub-Admins',
        'Keeps every batch\'s communication organized and separate',
      ],
      image: facultyNetworkDemo,
    },
    {
      icon: Library,
      title: 'Campus Library',
      description: 'A searchable digital catalog of your institution\'s physical book collection',
      features: [
        'Search by title, author, genre, or subject',
        'Check availability before visiting in person',
        'Managed by a dedicated Librarian role',
        'Separate from the Digital E-Library\'s online content',
        'Kept accurate with issue and return tracking',
      ],
      image: eLibraryPreview,
    },
    {
      icon: FileText,
      title: 'Documents & Certificates',
      description: 'Design certificates, ID cards, and letters that auto-fill each person\'s own details',
      features: [
        'Drag-and-drop template designer with a background image',
        'Wildcards auto-fill name, email, and ID for every recipient',
        'Students and faculty self-serve download from the app',
        'Restrict access to specific IDs, or leave it open to everyone',
        'No more manual certificate requests to the office',
      ],
      image: fileManagerDemo,
    },
    {
      icon: HelpCircle,
      title: 'AI-Guide',
      description: 'Publish official answers that UniAI can respond with, instantly',
      features: [
        'Publish News & Updates and General Info content',
        'One live version per category — always up to date',
        'Answered directly inside the UniAI chat\'s Guide mode',
        'No need to check a separate noticeboard or website',
        'Managed by Admins and Super Admins',
      ],
      image: eLibraryPreview,
    },
    {
      icon: MessagesSquare,
      title: 'Groups & Channels',
      description: 'WhatsApp-style group chats, topic channels, and broadcast-only Super Groups',
      features: [
        'Any student or faculty member can create a group',
        'Join instantly with a shared group code',
        'Channels split conversations by topic inside a group',
        'Super Groups broadcast one-way for large announcements',
        'Admin controls for managing members and admins',
      ],
      image: messagingDemo,
    },
    {
      icon: StickyNote,
      title: 'Notes',
      description: 'Upload, search, and share study notes across your institution',
      features: [
        'Upload notes for classmates or students to access',
        'Search by subject or keyword',
        'Manage or remove your own uploads at any time',
        'Available right from the mobile Library tab',
        'A shared study resource, not tied to any one classroom',
      ],
      image: fileManagerDemo,
    },
    {
      icon: ShieldCheck,
      title: 'Access Control & Roles',
      description: 'A clear permission hierarchy, from Super Admin down to Faculty and Librarian',
      features: [
        'Super Admin, Admin, Sub-Admin, Faculty, and Librarian roles',
        'Each role has a precisely scoped set of permissions',
        'Only Super Admins can create or modify Admin accounts',
        'Keeps sensitive actions properly gated by responsibility',
        'A clear, auditable structure as your institution grows',
      ],
      image: facultyNetworkDemo,
    },
    {
      icon: Briefcase,
      title: 'Placement Cell',
      description: 'A verified campus job board — post listings, let students apply, track outcomes',
      features: [
        'Post full-time and internship listings',
        'Students apply and track applications in the app',
        'Filter by type, mode (remote/on-site/hybrid), and category',
        'Revoke placement access for already-placed students',
        'Faculty can monitor all active listings',
      ],
      image: studyGroupsDemo,
    },
    {
      icon: UserCheck,
      title: 'Alumni Programme',
      description: 'Maintain a living directory of your graduates and track career outcomes',
      features: [
        'Auto-add students to alumni on removal',
        'Alumni submit career update surveys',
        'Track employment, company, and location',
        'Searchable alumni directory for the institution',
        'Permanent records that never expire',
      ],
      image: facultyNetworkDemo,
    },
    {
      icon: Sparkles,
      title: 'UniAI — Built-in AI',
      description: 'AI assistance for every user — study tools for students, content tools for faculty',
      features: [
        'Conversational AI for any academic question',
        'Generate mock exam questions for any subject',
        'Summarize long documents and lecture notes',
        'Faculty draft announcements and teaching content',
        'No external subscription — available to every user',
      ],
      image: eLibraryPreview,
    },
  ];

  return (
    <div className="flex flex-col ">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] md:py-16 py-12 bg-brand-yellow py-12">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
            backgroundImage: 'radial-gradient(circle, #004CFF 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        <div className="container relative z-10">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-5xl font-extrabold tracking-tight text-brand-black md:text-6xl">
              Our Services
            </h1>
            <p className="text-xl font-medium text-brand-black md:text-2xl">
              Comprehensive tools designed to enhance your educational journey
            </p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section
        className="relative bg-brand-white py-12"
        style={{
          transform: `translateY(${scrollY * 0.1}px)`,
        }}
      >
        <div className="container">
          <div className="mb-8 text-center">
            <h2 className="mb-3 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black">
              Everything You Need in One Platform
            </h2>
            <p className="text-xl font-medium text-brand-black">
              UNILinkd brings together all the essential tools for modern education
            </p>
          </div>

          <Tabs defaultValue="real-time-messaging" className="w-full mt-12">
            <div className="mb-10 rounded-2xl bg-gradient-to-r from-brand-yellow to-[#FFE566] p-1.5 shadow-lg">
              <TabsList className="h-auto w-full flex-wrap gap-1.5 bg-transparent lg:h-auto lg:w-full lg:flex-wrap lg:justify-start lg:p-0">
                {services.map((service, index) => (
                  <TabsTrigger
                    key={index}
                    value={service.title.toLowerCase().replace(/\s+/g, '-')}
                    className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg"
                  >
                    <service.icon className="mr-1.5 h-4 w-4 shrink-0" />
                    <span className="hidden truncate text-sm sm:inline">{service.title}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {services.map((service, index) => (
              <TabsContent
                key={index}
                value={service.title.toLowerCase().replace(/\s+/g, '-')}
                className="space-y-6"
              >
                <div className="grid gap-8 lg:grid-cols-2">
                  <div className="space-y-5">
                    <div>
                      <div className="mb-4 inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-lg">
                        <service.icon className="h-10 w-10 text-brand-white" />
                      </div>
                      <h3 className="mb-2 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">{service.title}</h3>
                      <p className="text-xl font-medium text-brand-black">{service.description}</p>
                    </div>

                    <div>
                      <h4 className="mb-3 text-lg font-bold text-brand-black">Key Features:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start">
                            <CheckCircle className="mr-3 mt-0.5 h-6 w-6 flex-shrink-0 text-brand-blue" />
                            <span className="font-medium text-brand-black">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="flex items-center justify-center">
                    <div className="relative">
                      <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                      <img
                        src={service.image}
                        alt={service.title}
                        className="relative rounded-3xl shadow-2xl transition-transform hover:scale-105"
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] md:py-16 py-12 bg-brand-yellow py-12  mt-12">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            //transform: `translateY(${scrollY * 0.3}px)`,
            backgroundImage: 'linear-gradient(45deg, #004CFF 25%, transparent 25%, transparent 75%, #004CFF 75%, #004CFF), linear-gradient(45deg, #004CFF 25%, transparent 25%, transparent 75%, #004CFF 75%, #004CFF)',
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px',
          }}
        />
        <div className="container relative z-10">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-6 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black">
              Why Choose UNILinkd?
            </h2>
            <div className="grid gap-6 md:grid-cols-3">
              <Card className="rounded-3xl bg-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-brand-black">All-in-One</CardTitle>
                  <CardDescription className="text-base font-medium text-brand-black">
                    Everything you need in a single, integrated platform
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card className="rounded-3xl bg-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-brand-black">User-Friendly</CardTitle>
                  <CardDescription className="text-base font-medium text-brand-black">
                    Intuitive design that anyone can use from day one
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card className="rounded-3xl bg-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-brand-black">Always Available</CardTitle>
                  <CardDescription className="text-base font-medium text-brand-black">
                    Access your resources anytime, anywhere, on any device
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
